import {
  collectFieldErrors,
  emailRule,
  isPositiveInt,
  isValidDate,
  isValidTime,
  phoneRule,
  type FieldErrors,
  type FieldSpec,
} from "@shared/validators";
import { MAX_TECH_RIDER_BYTES, type PackageFormData, type Step } from "./types";
import type {
  InquiryPackQuestionConfig,
  InquiryProfileConfig,
} from "./inquiry-profile-schema";
import { activeQuestions } from "./active-questions";

/**
 * One validator per step, all live here. Steps own no validation logic
 * inline — they call the matching `validate*` helper and render the
 * resulting per-field error map. Primitives come from @shared/validators;
 * step-level shape rules (cross-field, conditional requireds) live here.
 *
 * A field is valid iff it has no key in the map. A step is valid iff the
 * map is empty. The message stored under each key is the *exact* string
 * the UI renders — so the empty-vs-malformed distinction lives here, in the
 * validator, not in the component.
 */

// `checkField`, `Rule`, and `FieldErrors` live in @shared/validators so web
// and admin share one copy.

export type EventDetailsField =
  | "eventType"
  | "eventTypeOther"
  | "eventName"
  | "eventDate"
  | "eventEndDate"
  | "eventStartTime"
  | "eventEndTime";

export function validateEventDetails(
  formData: Partial<PackageFormData>,
): FieldErrors<EventDetailsField> {
  const specs: Partial<Record<EventDetailsField, FieldSpec>> = {
    eventType: {
      value: formData.eventType,
      rule: { required: true, emptyMsg: "Please select an event type" },
    },
    eventName: { value: formData.eventName, rule: { required: true } },
    eventDate: {
      value: formData.eventDate,
      rule: {
        required: true,
        predicate: isValidDate,
        invalidMsg: "Enter a valid date (DD/MM/YYYY)",
      },
    },
    eventEndDate: {
      value: formData.eventEndDate,
      rule: {
        predicate: isValidDate,
        invalidMsg: "Enter a valid date (DD/MM/YYYY) or leave blank",
      },
    },
    eventStartTime: {
      value: formData.eventStartTime,
      rule: {
        predicate: isValidTime,
        invalidMsg: "Enter a valid time (e.g. 8:00 PM) or leave blank",
      },
    },
    eventEndTime: {
      value: formData.eventEndTime,
      rule: {
        predicate: isValidTime,
        invalidMsg: "Enter a valid time (e.g. 8:00 PM) or leave blank",
      },
    },
  };
  // Cross-field: "other" event type requires a free-text specifier.
  if (formData.eventType === "other") {
    specs.eventTypeOther = {
      value: formData.eventTypeOther,
      rule: { required: true, emptyMsg: "Please specify the event type" },
    };
  }

  const errors = collectFieldErrors(specs);

  // Cross-field: a non-blank, well-formed end date that precedes the start. The
  // input's `min` bound was advisory-only, so a typed-out-of-order date slipped
  // through both the parse check and the old gate. Only applies when the end
  // date itself parsed (no format error already sitting under the key).
  if (
    !errors.eventEndDate &&
    formData.eventDate &&
    formData.eventEndDate &&
    formData.eventEndDate < formData.eventDate
  ) {
    errors.eventEndDate = "End date must be on or after the start date";
  }

  return errors;
}

export type VenueField =
  | "location"
  | "attendance"
  | "indoorOutdoor"
  | "powerAccess"
  | "siteAvailableFrom"
  | "packOutTime";

export function validateVenueStep(
  formData: Partial<PackageFormData>,
  profile: InquiryProfileConfig,
): FieldErrors<VenueField> {
  const scale = profile.venueStep.scale;
  const timeInvalidMessage = profile.venueStep.timing.invalidTimeMessage;
  const specs: Partial<Record<VenueField, FieldSpec>> = {};

  // The send rejects a full brief with no location, so the step does too.
  specs.location = {
    value: formData.location,
    rule: { required: true, emptyMsg: "Please enter the venue or address" },
  };

  // The profile scale field maps to the flat backend `attendance` key for the
  // live-sound profile. Empty stays undefined; malformed values stay numeric so
  // the validator can distinguish empty vs invalid copy. A chosen band
  // (including "unsure") stands in for the exact number — the step is valid
  // with either; a typed exact number must still be well-formed.
  const scaleValue = formData[scale.field];
  specs[scale.field] = {
    value: scaleValue === undefined || scaleValue === null ? "" : String(scaleValue),
    rule: {
      required: !formData.attendanceBand,
      emptyMsg: scale.emptyMessage,
      predicate: isPositiveInt,
      invalidMsg: scale.invalidMessage,
    },
  };

  specs.siteAvailableFrom = {
    value: formData.siteAvailableFrom,
    rule: {
      predicate: isValidTime,
      invalidMsg: timeInvalidMessage,
    },
  };
  specs.packOutTime = {
    value: formData.packOutTime,
    rule: {
      predicate: isValidTime,
      invalidMsg: timeInvalidMessage,
    },
  };

  const outdoorRule = profile.venueStep.outdoor.when;
  // Cross-field: power access is required only when the active profile's
  // environment rule says it is.
  if (formData[outdoorRule.field] === outdoorRule.equals) {
    specs.powerAccess = {
      value: formData.powerAccess,
      rule: {
        required: true,
        emptyMsg: profile.venueStep.outdoor.powerAccess.emptyMessage,
      },
    };
  }

  const errors = collectFieldErrors(specs);

  // indoorOutdoor is a radio/select, not a string field — a missing choice is
  // its own required message (no `checkField` value to trim).
  if (!formData.indoorOutdoor) {
    errors.indoorOutdoor = profile.venueStep.fields.environment.emptyMessage;
  }

  return errors;
}

export type ContactField =
  | "contactName"
  | "contactEmail"
  | "contactPhone"
  | "preferredContactMethod";

export function validateContactStep(
  formData: Partial<PackageFormData>,
): FieldErrors<ContactField> {
  return collectFieldErrors<ContactField>({
    contactName: { value: formData.contactName, rule: { required: true } },
    // The shared email/phone format rules, made required for the inquiry step
    // (contact details are mandatory here). The malformed message comes from
    // the one shared definition; only the required-ness is layered on locally.
    contactEmail: {
      value: formData.contactEmail,
      rule: { required: true, ...emailRule },
    },
    contactPhone: {
      value: formData.contactPhone,
      rule: { required: true, ...phoneRule },
    },
    // One tap, and the Company knows how to reach them; when suits is
    // optional and lives with the method (see getActiveInquiryFields).
    preferredContactMethod: {
      value: formData.preferredContactMethod,
      rule: { required: true, emptyMsg: "Choose how we should get back to you" },
    },
  });
}

export type ContentField = string;

/**
 * Validate the questions a Profile asks right now: a question the Customer
 * cannot see is neither required nor checked.
 */
export function validateContentStep(
  formData: Partial<PackageFormData>,
  profile: InquiryProfileConfig,
  riderAttached: boolean,
): FieldErrors<ContentField> {
  const answers = formData.profileAnswers ?? {};
  const errors: FieldErrors<ContentField> = {};
  const validateQuestion = (question: InquiryPackQuestionConfig): void => {
    const answer = answers[question.id];
    const answered =
      typeof answer === "boolean" ||
      (typeof answer === "string" && answer.trim() !== "");

    if (question.required && !answered) {
      errors[question.id] = `${question.label} is required`;
      return;
    }
    if (!answered) return;

    if (
      question.type === "integer" &&
      (typeof answer !== "string" || !/^\d+$/.test(answer))
    ) {
      errors[question.id] = "Enter a whole number of 0 or more";
    } else if (
      question.type === "select" &&
      (typeof answer !== "string" || !question.options?.includes(answer))
    ) {
      errors[question.id] = "Select an available option";
    }
  };

  for (const question of activeQuestions(profile, formData, riderAttached)) {
    validateQuestion(question);
  }
  return errors;
}

/**
 * The first step, in the profile's order, whose answers would not pass
 * its own Continue — or null when every step would. The summary panel and
 * the stepper can move the Customer past a step they never finished, so
 * sending is gated on this rather than on the contact step alone.
 */
export function firstInvalidStep(
  profile: InquiryProfileConfig,
  formData: Partial<PackageFormData>,
  riderAttached: boolean,
): Step | null {
  const errorsFor: Partial<Record<Step, () => object>> = {
    "event-details": () => validateEventDetails(formData),
    venue: () => validateVenueStep(formData, profile),
    content: () => validateContentStep(formData, profile, riderAttached),
    contact: () => validateContactStep(formData),
  };
  for (const { id } of profile.steps) {
    const errors = errorsFor[id]?.();
    if (errors && Object.keys(errors).length > 0) return id;
  }
  return null;
}

const ALLOWED_TECH_RIDER_EXTENSIONS = [".pdf", ".doc", ".docx"] as const;

export type TechRiderFileError = "wrong_extension" | "too_large";

/**
 * Both the file picker (extension) and the parsing hook (size) call this
 * so a file rejected in one place is rejected in the other. No more
 * silent passthrough of a 50MB PDF or programmatic .txt.
 */
export function validateTechRiderFile(
  file: File,
): { ok: true } | { ok: false; reason: TechRiderFileError; message: string } {
  const lower = file.name.toLowerCase();
  if (!ALLOWED_TECH_RIDER_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return {
      ok: false,
      reason: "wrong_extension",
      message: "Please upload a PDF, DOC, or DOCX file",
    };
  }
  if (file.size > MAX_TECH_RIDER_BYTES) {
    return { ok: false, reason: "too_large", message: "File exceeds 10 MB limit" };
  }
  return { ok: true };
}

export type AttendanceChangeResult =
  | { kind: "clear" }
  | { kind: "invalid"; count: number }
  | { kind: "update"; count: number };

/**
 * Maps a raw attendance input string to a state change. Crucially it no longer
 * collapses "empty" and "bad number" into one `reset` — clearing the field
 * yields `clear` (store `undefined`, so the validator sees empty), while a
 * non-empty but out-of-range value yields `invalid` (store the number, so the
 * validator sees a malformed entry and shows the distinct invalid message).
 */
export function computeAttendanceChange(raw: string): AttendanceChangeResult {
  const trimmed = raw.trim();
  if (trimmed === "") return { kind: "clear" };
  const count = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(count) || count < 1) {
    // Keep a sentinel number (0 when unparseable) so attendance stays non-empty
    // and the validator reports "invalid" rather than "empty".
    return { kind: "invalid", count: Number.isFinite(count) ? count : 0 };
  }
  return { kind: "update", count };
}

// True when the end date is set and precedes the (new) start date, i.e. the
// onChange handler should clear it. Dates are ISO (YYYY-MM-DD), so string
// comparison is correct and timezone-independent.
export function shouldClearEndDate(
  newStartDate: string,
  currentEndDate: string | undefined | null,
): boolean {
  return Boolean(newStartDate && currentEndDate && currentEndDate < newStartDate);
}
